/* Phase 4 — Floating dialer toggles.
 *
 * Two redundant entry points so agents notice the dialer on first login:
 *   - DialerEdgeHandle: thin vertical handle pinned to the right edge of
 *     the viewport. Phone icon + rotated "Dialer" label + pulse when
 *     a call is in progress.
 *   - DialerFloatingToggle: pill-shaped floating button anchored to the
 *     bottom-right corner. Always visible above other content.
 *
 * Both call useDialer().togglePanel().
 */
import React from "react";
import { Phone, ChevronUp, PhoneCall, PhoneOff } from "lucide-react";
import { useDialer } from "./DialerProvider";

export function DialerEdgeHandle() {
    const { open, status, togglePanel } = useDialer();
    if (open) return null;
    const inCall = status === "in_call" || status === "ringing" || status === "incoming";
    return (
        <button
            onClick={togglePanel}
            data-testid="dialer-edge-handle"
            aria-label="Open dialer"
            className="group fixed right-0 top-1/2 z-40 -translate-y-1/2 rounded-l-2xl border border-r-0 border-mm-line bg-mm-navy/95 py-3 pl-3 pr-2 text-white shadow-xl backdrop-blur transition-transform hover:-translate-x-0.5"
        >
            <div className="flex flex-col items-center gap-2">
                <Phone size={14} className={inCall ? "animate-pulse text-emerald-300" : "text-white"} />
                <span className="rotate-180 text-[10px] font-medium uppercase tracking-[0.18em]" style={{ writingMode: "vertical-rl" }}>
                    Dialer
                </span>
                {inCall && (
                    <span className="h-2 w-2 animate-ping rounded-full bg-emerald-400" />
                )}
            </div>
        </button>
    );
}

export function DialerFloatingToggle() {
    const { open, status, togglePanel, callTimer } = useDialer();
    const inCall = status === "in_call";
    const ringing = status === "ringing" || status === "incoming";
    return (
        <button
            onClick={togglePanel}
            data-testid="dialer-floating-toggle"
            className={[
                "fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium shadow-2xl transition-all",
                open
                    ? "bg-mm-navy text-white"
                    : inCall
                    ? "bg-emerald-600 text-white"
                    : ringing
                    ? "bg-amber-500 text-white"
                    : "bg-white text-mm-navy ring-1 ring-mm-line hover:bg-mm-bg",
            ].join(" ")}
        >
            {inCall ? (
                <><PhoneCall size={14} className="animate-pulse" /> {fmt(callTimer)}</>
            ) : ringing ? (
                <><PhoneCall size={14} className="animate-bounce" /> Ringing…</>
            ) : open ? (
                <><PhoneOff size={14} /> Close dialer <ChevronUp size={12} /></>
            ) : (
                <><Phone size={14} /> Dialer</>
            )}
        </button>
    );
}

function fmt(s) {
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
}
